import {
  GameEngine,
  REPLAY_ENGINE_VERSION,
  asCardFoil,
  parseAvatarConfig,
  type CardData,
  type DuelReplay,
  type GameEvent,
  type PlayerAction,
  type PlayerInfo,
  type Seat,
} from '@shared/index'
import lobData from '../../assets/lob-cards.json'
import type { GameClient, GameClientHandlers } from './GameClient'

function toCardData(raw: Record<string, unknown>): CardData {
  const card: CardData = {
    id: String(raw.id),
    name: String(raw.name ?? raw.id),
    type: String(raw.type ?? 'Effect Monster'),
  }
  if (typeof raw.level === 'number') card.level = raw.level
  if (Array.isArray(raw.typeline)) card.typeline = raw.typeline.map(String)
  if (typeof raw.humanReadableCardType === 'string') card.humanReadableCardType = raw.humanReadableCardType
  if (typeof raw.desc === 'string') card.desc = raw.desc
  if (typeof raw.atk === 'number') card.atk = raw.atk
  if (typeof raw.def === 'number') card.def = raw.def
  if (typeof raw.race === 'string') card.race = raw.race
  if (typeof raw.attribute === 'string') card.attribute = raw.attribute
  if (typeof raw.archetype === 'string') card.archetype = raw.archetype
  return card
}

const LOB_BY_ID = new Map(
  (lobData as { data: Record<string, unknown>[] }).data.map((raw) => [
    String(raw.id),
    toCardData(raw),
  ]),
)

function cardsOf(
  ids: string[],
  foils: string[] | undefined,
): CardData[] {
  return ids.map((id, index) => {
    const base = LOB_BY_ID.get(id) ?? { id, name: id, type: 'Effect Monster' }
    const foil = foils?.[index]
    return foil ? { ...base, foil: asCardFoil(foil) } : { ...base }
  })
}

/**
 * Replays a stored duel by re-applying its actions on a fresh engine.
 * The scene animates the same events a live duel would emit.
 */
export class ReplayClient implements GameClient {
  readonly seat: Seat = 0
  readonly watch = true
  private readonly handlerSets: GameClientHandlers[] = []
  private readonly engine: GameEngine
  private readonly actions: { s: Seat; a: PlayerAction }[]
  private index = 0
  private playing = true
  private animating = false
  private pendingOver = false
  private finished = false
  private idleToken = 0
  private failed = false

  constructor(replay: DuelReplay) {
    if (replay.version !== REPLAY_ENGINE_VERSION) {
      throw new Error('replay version')
    }
    const seats: [PlayerInfo, PlayerInfo] = [0, 1].map((seat) => ({
      id: `replay-${seat}`,
      username: replay.names[seat] ?? 'Duelist',
      isAI: false,
      connected: true,
      avatar: parseAvatarConfig(undefined),
    })) as [PlayerInfo, PlayerInfo]
    const catalog = [...LOB_BY_ID.values()]
    this.engine = GameEngine.createMatch({
      id: 'replay',
      seats,
      decks: [
        cardsOf(replay.decks[0].main, replay.decks[0].mainFoils),
        cardsOf(replay.decks[1].main, replay.decks[1].mainFoils),
      ],
      extraDecks: [
        cardsOf(replay.decks[0].extra, replay.decks[0].extraFoils),
        cardsOf(replay.decks[1].extra, replay.decks[1].extraFoils),
      ],
      catalog,
      seed: replay.seed,
      firstPlayer: replay.firstPlayer,
    })
    this.actions = replay.actions
    if (this.engine.getState().status === 'finished') this.pendingOver = true
  }

  setHandlers(handlers: GameClientHandlers): void {
    this.handlerSets.push(handlers)
  }

  dropHandlers(): void {
    this.handlerSets.length = 0
  }

  /** Scene finished the current batch. Step the next action when playing. */
  onBoardIdle(): void {
    this.animating = false
    const token = ++this.idleToken
    queueMicrotask(() => {
      if (token !== this.idleToken || this.animating || this.finished) return
      this.kick()
    })
  }

  setPlaying(on: boolean): void {
    this.playing = on
    if (on && !this.animating) this.kick()
  }

  isPlaying(): boolean {
    return this.playing
  }

  /** One action, only while paused and the board is idle. */
  stepOnce(): void {
    if (this.playing || this.animating || this.finished) return
    this.step()
  }

  start(): void {
    this.fire('onStart', {
      state: this.engine.getStateForSpectator(),
      viewerSeat: 0,
    })
    if (this.pendingOver && this.actions.length === 0) this.finish()
  }

  send(_action: PlayerAction): void {}

  requestState(): void {
    this.fire('onState', {
      state: this.engine.getStateForSpectator(),
      viewerSeat: 0,
    })
  }

  dispose(): void {
    this.finished = true
    this.playing = false
    this.handlerSets.length = 0
  }

  private kick(): void {
    if (this.finished || this.failed) return
    if (this.pendingOver && this.index >= this.actions.length) {
      this.finish()
      return
    }
    if (!this.playing) return
    this.step()
  }

  private step(): void {
    if (this.finished || this.failed || this.animating) return
    if (this.index >= this.actions.length) {
      this.pendingOver = true
      this.finish()
      return
    }
    const row = this.actions[this.index]!
    this.index += 1
    this.animating = true
    let events: GameEvent[]
    try {
      events = this.engine.applyAction(row.s, row.a)
    } catch {
      this.animating = false
      this.failed = true
      this.playing = false
      this.fire('onError', 'replay failed', 'replayFailed')
      return
    }
    const state = this.engine.getStateForSpectator()
    if (this.engine.getState().status === 'finished') this.pendingOver = true
    if (events.length === 0) {
      this.animating = false
      this.fire('onState', { state, viewerSeat: 0 })
      if (this.pendingOver) {
        this.finish()
        return
      }
      if (this.playing) queueMicrotask(() => this.kick())
      return
    }
    this.fire('onEvents', { events, state, viewerSeat: 0, replay: true })
  }

  private finish(): void {
    if (this.finished) return
    this.finished = true
    this.playing = false
    const snap = this.engine.getState()
    this.fire('onOver', {
      winner: snap.winner,
      winReason: snap.winReason,
    })
  }

  private fire<K extends keyof GameClientHandlers>(
    key: K,
    ...args: Parameters<NonNullable<GameClientHandlers[K]>>
  ): void {
    for (const set of this.handlerSets) {
      const fn = set[key] as ((...a: typeof args) => void) | undefined
      fn?.(...args)
    }
  }
}
